import { createTheme, responsiveFontSizes } from '@mui/material'
import { blue, grey } from '@mui/material/colors'

// Interface declaration on typescript to expan color palette
declare module '@mui/material' {
  interface Palette {
    dense?: Palette['primary']
    facebook?: Palette['primary']
    flat: Palette['primary']
    google?: Palette['primary']
    idle?: Palette['primary']
    linkedin?: Palette['primary']
    neutral?: Palette['primary']
  }
  interface PaletteOptions {
    dense?: Palette['primary']
    facebook?: PaletteOptions['primary']
    flat?: PaletteOptions['primary']
    google?: PaletteOptions['primary']
    idle?: PaletteOptions['primary']
    linkedin?: PaletteOptions['primary']
    neutral?: PaletteOptions['primary']
  }
}

// Udpate Button's color prop options
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    facebook?: true
    google?: true
    idle?: true
    linkedin?: true
    neutral?: true
  }
}

declare module '@mui/material/AppBar' {
  interface AppBarPropsColorOverrides {
    dense?: true
    flat?: true
  }
}

const themeConfig = createTheme({
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&.MuiAccordion-root': {
            '&:after': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              bottom: 1,
              content: `""`,
              height: '1px',
              left: 0,
              opacity: 1,
              position: 'absolute',
              right: 0,
            },
            '&:before': {
              display: 'none !important',
              opacity: 0,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          '&.MuiAppBar-colorFlat': {
            borderBottom: '2px solid #DCDCF0',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        square: {
          borderRadius: '10px',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: blue[50],
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === 'outlined' && {
            border: 'none',
            borderRadius: '10px',
            boxShadow:
              'rgba(0, 0, 0, 0.04) 0px 5px 22px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px',
          }),
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#DEDCD9',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1D1E2C',
            },
            '&:hover fieldset': {
              borderColor: '#1D1E2C',
            },
          },
          '& label.Mui-focused': {
            color: '#1D1E2C',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: '1',
          },
          '&.MuiListItemButton-card': {
            borderBottom: '1px solid #d4e9ff',
            borderLeft: '5px solid #2196f3',
            borderRadius: 5,
            borderRight: '1px solid #d4e9ff',
            borderTop: '1px solid #d4e9ff',
            margin: '5px 0 5px',
            paddingLeft: 5,
          },
          '&.MuiListItemButton-card:first-of-type': {
            marginTop: 0,
          },
          '&.MuiListItemButton-card:last-child': {
            marginBottom: 0,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#DEDCD9',
            },

            '&.Mui-focused fieldset': {
              borderColor: '#1D1E2C',
            },
            '&:hover fieldset': {
              borderColor: '#1D1E2C',
            },
            backgroundColor: '#FFF',
          },
          '& label.Mui-focused': {
            color: '#1D1E2C',
          },
        },
      },
    },
  },
  palette: {
    dense: {
      contrastText: '#FAF9F9',
      dark: '#1D1E2C',
      light: '#1D1E2C',
      main: '#1D1E2C',
    },
    flat: {
      contrastText: grey[900],
      dark: '#1D1E2C',
      light: '#1D1E2C',
      main: '#FFFFFF',
    },
    idle: {
      main: grey[600],
    },
    neutral: {
      contrastText: grey[900],
      main: grey[300],
    },
    secondary: {
      contrastText: '#FAF9F9',
      main: '#1D1E2C',
    },
  },
  typography: {
    button: {
      fontWeight: 700,
    },
    fontFamily: ['Manrope', 'Roboto', '-apple-system', 'sans-serif'].join(','),
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 700,
    },
    subtitle2: {
      fontWeight: 700,
    },
  },
})

const portalTheme = responsiveFontSizes(themeConfig)

export default portalTheme
